import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EventTypes } from '@models/server-common';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ApiService } from '@quorum/api';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { CommunicatorActions, CommunicatorFacade } from '@quorum/communicator-ngrx';
import {
  Address,
  AlternateTransportation,
  ServiceAppointment as Appointment,
  AppointmentCase,
  Associate,
  CombinedTax,
  Conversation,
  DealerCodeRooftopLink,
  DealerCodeType,
  Employee,
  Event,
  GstTax,
  Member,
  MessageTransactionMessagePreference,
  Note,
  OemRooftopLink,
  PartsRequest,
  PartsRequestDetail,
  PstTax,
  QuoteDetail,
  QuoteHeader,
  QuoteSalesDetail,
  ServiceAppointmentDeclinedMpviCode,
  ServiceHistoryDashboard,
  TokenUser,
  Vehicle,
  WorkOrder,
  WorkOrderCase,
  WorkorderDeclinedMpviCode,
} from '@quorum/models/xs-resource';

import { AuthenticationStateService } from '@quorum/authentication/services';
import {
  AddressQueryParameters,
  AlternateTransportationQueryParameters,
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  ConversationQueryParameters,
  GstTaxQueryParameters,
  MessageTransactionMessagePreferenceQueryParameters,
  PstTaxQueryParameters,
  QuoteDetailQueryParameters,
  ServiceHistoryDashboardQueryParameters,
  UserQueryParameters,
} from '@quorum/models/xs-query';
import { OemIntegrationService } from '@quorum/quorum-api';
import {
  AddressEntityActionTypes,
  AssociatesEntityActionTypes,
  UpdateAddressOnServer,
  UpdateAddressOnServerSuccess,
  UpdateAssociateOnServer,
  UpdateAssociateOnServerSuccess,
} from '@quorum/sha-entities/associates';
import {
  CreateMessageTransactionMessagePreferenceOnServer,
  MessageTransactionMessagePreferenceEntityActionTypes,
  UpdateMessageTransactionMessagePreferenceOnServer,
} from '@quorum/sha-entities/communicator';
import {
  AppointmentCasesEntityActionTypes,
  AppointmentsEntityActionTypes,
  CreateAppointmentCaseOnServer,
  CreateAppointmentCaseOnServerSuccess as CreateAppointmentCaseOnServerSuccess2,
  CreateAppointmentOnServer,
  CreateAppointmentOnServerSuccess,
  CreateQuoteLineOnServer,
  CreateQuoteLineOnServerSuccess,
  CreateServiceAppointmentDeclinedOperationsOnServer,
  CreateWorkOrderCaseOnServer,
  CreateWorkOrderDeclinedOperationsOnServer,
  DeleteAppointmentCaseOnServer,
  DeleteAppointmentCaseOnServerSuccess,
  DeleteAppointmentFromServer,
  DeleteAppointmentFromServerSuccess,
  DeleteQuoteLineFromServer,
  DeleteSelectedAppointmentDeclinedMpviFromServer,
  DeleteWorkOrderCaseFromServer,
  DeleteWorkOrderDeclinedOperationFromServer,
  GetAppointmentCasesFromServerSuccess,
  GetQuoteLinesFromServer,
  GetQuoteLinesFromServerSuccess,
  QuoteLinesEntityActionTypes,
  UpdateAppointmentCaseOnServer,
  UpdateAppointmentCaseOnServerSuccess,
  UpdateAppointmentOnServer,
  UpdateAppointmentOnServerError,
  UpdateAppointmentOnServerSuccess,
  UpdateDeclinedReasonForQuoteLineOnServer,
  UpdateDeclinedReasonForQuoteLineOnServerSuccess,
  UpdateQuoteLineOnServer,
  UpdateQuoteLineOnServerSuccess,
  UpdateSelectedAppointmentDeclinedMpviOnServer,
  UpdateWorkOrderDeclinedOperationOnServer,
} from '@quorum/sha-entities/fixed-operations';
import {
  CreateVehicleOnServer,
  CreateVehicleOnServerSuccess,
  GetVehicleFromServer,
  UpdateVehicleOnServer,
  UpdateVehicleOnServerSuccess,
  VehiclesEntityActionTypes,
} from '@quorum/sha-entities/vehicle';
import { SystemControlStateService } from '@quorum/xsr-system-control/services';
import {
  ADD_PENDING_ACCEPTED_SERVICE_IN_STATE,
  AddPendingAcceptedServiceInState,
} from 'libs/xsr-vehicle-maintenance/src/lib/+state/pending-accepted-services/pending-accepted-services.actions';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import * as convert from 'xml-js';
import {
  AddApprovedQuoteLineInState,
  AddDeclinedQuoteLineInState,
  ClearAppointmentState,
  ClearCustomerAndVehicleState,
  CreateAlternateTransportationOnServer,
  CreateAppointmentCaseFromQuoteLine,
  CreateAppointmentCaseFromQuoteLineSuccess,
  CreateAppointmentCaseOnServerSuccess,
  CreateAppointmentCasesFromQuoteLines,
  CreateAppointmentOnServerSuccess as CreateAppointmentOnServerSuccess2,
  CreateNotesOnServer,
  CreatePartDetailForQuoteLine,
  CreatePartsRequestDetailForQuoteLine,
  CreateVehicleWithAssociateAndAddressFromServer,
  DeleteAlternateTransportationOnServer,
  DeleteAppointmentFromServerSuccess as DeleteAppointmentFromServerSuccess2,
  DeleteSelectedAppointmentCaseOnServerSuccess,
  DeleteTodaysAppointmentFromServerSuccess,
  GetAllNotesFromServer,
  GetAlternateTransportationsFromServer,
  GetSelectedAppointmentCasesFromServerSuccess,
  GetSelectedAppointmentFromServer,
  GetTodaysAppointmentsFromServer,
  GetUnacknowledgedAppointmentsFromServer,
  RemoveApprovedQuoteLineInState,
  RemoveDeclinedQuoteLineInState,
  RemoveQuoteFromState,
  ServiceLaneActionTypes,
  UpdateQuoteLineOnServerSuccess as ServiceLaneQuoteUpdate,
  SetUnsavedLineItemInState,
  SetVehicle,
  SetVerifyCustomerCreateWOButtonEnabledInState,
  SetWorkOrderRequiredFieldsPresentInState,
  TriggerCreateWorkOrderProcess,
  UpdateAlternateTransportationOnServer,
  UpdateAppointmentFiltersInState,
  UpdateApprovedQuoteLineInState,
  UpdateCustomerAddressOnServerSuccess,
  UpdateCustomerAssociateOnServerSuccess,
  UpdateDeclinedQuoteLineInState,
  UpdateDeclinedReasonForQuoteLineInState,
  UpdateNotesOnServer,
  UpdateQuoteLineInState,
  UpdateSelectedAppointmentCaseOnServerSuccess,
  UpdateSelectedAppointmentOnServerSuccess,
  UpdateTodaysAppointmentInState,
  UpdateUnacknowledgedAppointmentInState,
} from './service-lane.actions';
import { ServiceLanePartialState } from './service-lane.reducer';
import { serviceLaneQuery } from './service-lane.selectors';

const DASHBOARD_SUPPORTED_OEMS = ['GM', 'FO', 'TOY', 'CH', 'NIS', 'HYU'];
@Injectable()
export class ServiceLaneFacade {
  private toastSubject = new Subject<any>();

  campaignsToCreate$: BehaviorSubject<{
    campaignData: { quoteDetailForCampaign: QuoteDetail; campaignDate: string }[];
    loading: boolean;
  }> = new BehaviorSubject<{
    campaignData: { quoteDetailForCampaign: QuoteDetail; campaignDate: string }[];
    loading: boolean;
  }>({
    campaignData: [
      {
        quoteDetailForCampaign: undefined,
        campaignDate: undefined,
      },
    ],
    loading: true,
  });

  dealerBac$: Observable<string> = combineLatest([
    this.apiService.get<DealerCodeType[]>(`v/1/system/dealer-code-types`, { params: { code: 'bac' } }).pipe(
      map((dealerCodeTypes: DealerCodeType[]) => dealerCodeTypes[0]?.id),
      filter((id) => !!id)
    ),
    this.authenticationStateService.selectAuthenticatedEmployee().pipe(
      map((employee) => employee?.rooftopId),
      filter((rooftopId) => !!rooftopId),
      take(1)
    ),
  ]).pipe(
    switchMap(([dealerCodeTypeId, rooftopId]) => {
      return this.apiService
        .get<DealerCodeRooftopLink[]>(`/v/1/system/dealer-code-rooftop-links`, {
          params: { dealerCodeTypeId, rooftopId },
        })
        .pipe(map((bacDealerCodeRooftopLinks) => bacDealerCodeRooftopLinks[0]?.dealerCode || ''));
    })
  );

  dashboardData$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  noHyundaiDashboardData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  vehicle$ = this.store.pipe(select(serviceLaneQuery.getVehicle));
  customerAssociate$ = this.store.pipe(select(serviceLaneQuery.getCustomerAssociate));
  customerAddress$ = this.store.pipe(
    select(serviceLaneQuery.getCustomerAddress),
    map((address) => {
      if (address) {
        let adr = new DeepCopyPipe().transform(address);
        //delete adr.embedded;
        return adr;
      }
      return address;
    })
  );
  allNotes$ = this.store.pipe(select(serviceLaneQuery.getAllNotes));
  todaysAppointmentsLoading$ = this.store.pipe(select(serviceLaneQuery.getTodaysAppointmentsLoading));
  openAppointmentsLoading$ = this.store.pipe(select(serviceLaneQuery.getOpenAppointmentStatus));
  todaysAppointments$ = this.store.pipe(select(serviceLaneQuery.getTodaysAppointments));
  openAppointments$ = this.store.pipe(select(serviceLaneQuery.selectOpenAppointments));
  unacknowledgedAppointmentCards$ = this.store.pipe(select(serviceLaneQuery.getUnacknowledgedAppointmentCards));
  unacknowledgedAppointmentsStatus$ = this.store.pipe(select(serviceLaneQuery.getUnacknowledgedAppointmentStatus));
  selectedAppointmentCasesStatus$ = this.store.pipe(select(serviceLaneQuery.getSelectedAppointmentCasesStatus));
  selectedAppointmentCasesError$ = this.store.pipe(select(serviceLaneQuery.getSelectedAppointmentCasesError));
  myAppointmentsToday$ = this.store.pipe(select(serviceLaneQuery.getMyAppointments));
  selectedAppointment$ = this.store.pipe(select(serviceLaneQuery.getSelectedAppointment));
  selectedAppointmentCases$ = this.store.pipe(select(serviceLaneQuery.getSeletedAppointmentCases));
  getSelectedAppointmentCasesWithServiceGroupAndRate$ = this.store.pipe(
    select(serviceLaneQuery.getSelectedAppointmentCasesWithServiceGroupAndRate)
  );
  serviceHistory$ = this.store.pipe(select(serviceLaneQuery.getServiceHistory));
  serviceGroups$ = this.store.pipe(select(serviceLaneQuery.getServiceGroups));
  quoteLines$ = this.store.pipe(select(serviceLaneQuery.getQuoteLines));
  getAllNonExpiredQuoteLines$ = this.store.pipe(select(serviceLaneQuery.getAllNonExpiredQuoteLines));
  approvedQuoteLines$ = this.store.pipe(select(serviceLaneQuery.getApprovedQuoteLines));
  approvedQuoteLinesWithServiceGroup$ = this.store.pipe(select(serviceLaneQuery.getApprovedQuoteLinesWithServiceGroup));
  declinedQuoteLines$ = this.store.pipe(select(serviceLaneQuery.getDeclinedQuoteLines));
  menuQuoteLines$ = this.store.pipe(select(serviceLaneQuery.getMenuQuoteLines));
  menuQuoteLinesWithStatuses$ = this.store.pipe(
    select(serviceLaneQuery.getMenuQuoteLinesWithStatuses),
    map((quoteLines) => new DeepCopyPipe().transform(quoteLines))
  );
  campaignQuoteLinesWithStatuses$ = this.store.pipe(
    select(serviceLaneQuery.getCampaignQuoteLinesWithStatuses),
    map((quoteLines) => new DeepCopyPipe().transform(quoteLines))
  );

  mpviQuoteLines$ = this.store.pipe(
    select(serviceLaneQuery.getMPVIQuoteLines),
    map((quoteLines) => new DeepCopyPipe().transform(quoteLines))
  );
  mpviQuoteLinesWithStatuses$ = this.store.pipe(
    select(serviceLaneQuery.getMPVIQuoteLinesWithStatuses),
    map((quoteLines) => new DeepCopyPipe().transform(quoteLines))
  );
  quoteLineActionedPercentages$ = this.store.pipe(select(serviceLaneQuery.getQuoteLineActionedPercentages));
  serviceAppointmentDeclinedOperations$ = this.store.pipe(
    select(serviceLaneQuery.getServiceAppointmentDeclinedOperations)
  );
  ui$ = this.store.pipe(select(serviceLaneQuery.getUi));
  appointmentFilters$ = this.store.pipe(select(serviceLaneQuery.getAppointmentFilters));
  getAllDeclinedServicesForVehicle$ = this.store.pipe(select(serviceLaneQuery.getAllDeclinedServicesForVehicle));
  alternateTransportations$ = this.store.pipe(select(serviceLaneQuery.getAlternateTransportations));

  setSelectedAppointmentCases$ = this.actions$.pipe(
    ofType(AppointmentCasesEntityActionTypes.GetAppointmentCasesFromServerSuccess),
    tap((action: GetAppointmentCasesFromServerSuccess) => {
      this.setSelectedAppointmentCases(action.payload);
    })
  );

  createAppointmentCaseFromQuoteLineSuccess$ = this.actions$.pipe(
    ofType(ServiceLaneActionTypes.CreateAppointmentCaseFromQuoteLineSuccess),
    tap((action: CreateAppointmentCaseFromQuoteLineSuccess) => {
      this.removeApprovedQuoteLine(action.payload.quoteLine);
      this.createAppointmentCase(action.payload.appointmentCase, action.payload.quoteLine);
    })
  );
  // updateQuoteLineSuccess$ = this.actions$.pipe(
  //   ofType(QuoteLinesEntityActionTypes.UpdateQuoteLineOnServerSuccess),
  //   tap((action: UpdateQuoteLineOnServerSuccess) => {
  //     console.log('NEED TO REMOVE DECLINED QUOTED LINE BECAUSE ITS COMPLETE');
  //     this.removeDeclinedQuoteLine(action.payload);
  //   })
  // );
  updateSelectedAppointmentCase$ = this.actions$.pipe(
    ofType(AppointmentCasesEntityActionTypes.UpdateAppointmentCaseOnServerSuccess),
    tap((action: UpdateAppointmentCaseOnServerSuccess) => {
      this.updateSelectedAppointmentCase(action.payload);
    })
  );
  deleteSelectedAppointmentCase$ = this.actions$.pipe(
    ofType(AppointmentCasesEntityActionTypes.DeleteAppointmentCaseOnServerSuccess),
    tap((action: DeleteAppointmentCaseOnServerSuccess) => {
      this.deleteSelectedAppointmentCase(action.payload);
    })
  );
  createAppointmentCase$ = this.actions$.pipe(
    ofType(AppointmentCasesEntityActionTypes.CreateAppointmentCaseOnServerSuccess),
    tap((action: CreateAppointmentCaseOnServerSuccess2) => {
      this.createAppointmentCase(action.payload.appointmentCase, action.payload.quoteLine);
    })
  );
  updateSelectedAppointment$ = this.actions$.pipe(
    ofType(AppointmentsEntityActionTypes.UpdateAppointmentOnServerSuccess),
    tap((action: UpdateAppointmentOnServerSuccess) => {
      this.updateAppointment(action.payload);
    })
  );
  public updateSelectedAppointmentError$ = this.actions$.pipe(
    ofType<UpdateAppointmentOnServerError>(AppointmentsEntityActionTypes.UpdateAppointmentOnServerError)
  );
  updateQuoteLine$ = this.actions$.pipe(
    ofType(QuoteLinesEntityActionTypes.UpdateQuoteLineOnServerSuccess),
    tap((action: UpdateQuoteLineOnServerSuccess) => {
      this.updateQuoteLine(action.payload);
    })
  );
  createAppointment$ = this.actions$.pipe(
    ofType(AppointmentsEntityActionTypes.CreateAppointmentOnServerSuccess),
    tap((action: CreateAppointmentOnServerSuccess) => {
      this.createAppointment(action.payload);
    })
  );

  listenForCreateAppointmentOnServerSuccess$ = this.actions$.pipe(
    ofType(ServiceLaneActionTypes.CreateAppointmentOnServerSuccess),
    map((action: CreateAppointmentOnServerSuccess) => {
      return action.payload;
    })
  );

  listenForCreateConversationSuccess$ = this.actions$.pipe(
    ofType(CommunicatorActions.createConversationSuccess),
    map((action) => {
      return action;
    })
  );

  deleteAppointment$ = this.actions$.pipe(
    ofType(AppointmentsEntityActionTypes.DeleteAppointmentFromServerSuccess),
    tap((action: DeleteAppointmentFromServerSuccess) => {
      this.deleteAppointment(action.payload.appointment);
    })
  );
  deleteTodaysAppointment$ = this.actions$.pipe(
    ofType(AppointmentsEntityActionTypes.DeleteAppointmentFromServerSuccess),
    tap((action: DeleteAppointmentFromServerSuccess) => {
      this.deleteTodaysAppointment(action.payload.appointment);
    })
  );

  updateAssociate$ = this.actions$.pipe(
    ofType(AssociatesEntityActionTypes.UpdateAssociateOnServerSuccess),
    tap((action: UpdateAssociateOnServerSuccess) => {
      this.updateAssociate(action.payload);
    })
  );
  updateAddress$ = this.actions$.pipe(
    ofType(AddressEntityActionTypes.UpdateAddressOnServerSuccess),
    tap((action: UpdateAddressOnServerSuccess) => {
      this.updateAddress(action.payload);
    })
  );
  createVehicle$ = this.actions$.pipe(
    ofType(
      VehiclesEntityActionTypes.CreateVehicleOnServerSuccess,
      ServiceLaneActionTypes.CreateVehicleWithAssociateAndAddressFromServerSuccess
    ),
    tap((action: CreateVehicleOnServerSuccess) => {
      //this.createAppointment(action.payload);
    })
  );
  updateVehicle$ = this.actions$.pipe(
    ofType(VehiclesEntityActionTypes.UpdateVehicleOnServerSuccess),
    tap((action: UpdateVehicleOnServerSuccess) => {
      this.setVehicle(action.payload);
    })
  );
  addCreatedQuoteLineToState$ = this.actions$.pipe(
    ofType(QuoteLinesEntityActionTypes.CreateQuoteLineOnServerSuccess),
    tap((action: CreateQuoteLineOnServerSuccess) => {
      this.addQuoteLine(action.payload.quoteLine);
      this.updateApprovedQuoteLine(action.payload.quoteLine, action.payload.previousValue.id);
      this.updateDeclinedQuoteLine(action.payload.quoteLine, action.payload.previousValue.id);
    })
  );
  addQuoteToState$ = this.actions$.pipe(
    ofType(ServiceLaneActionTypes.SetVerifyCustomerCreateWOButtonEnabledInState),
    tap((action: SetVerifyCustomerCreateWOButtonEnabledInState) => {
      this.setVerifyCustomerCreateWOButtonEnabledInState(action.payload);
    })
  );
  dashboardSubject$: ReplaySubject<any> = new ReplaySubject(1);
  warrantyNotifications$: ReplaySubject<any> = new ReplaySubject(1);

  customerUsingSoftware$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
    filter((emp) => emp != null),
    take(1),
    switchMap((employee) => {
      return this.systemControlStateService
        .getSystemControlValues(['CL_CUSTOMER_USING_SOFTWARE'], employee.rooftopId)
        .pipe(
          take(1),
          map((scts) => {
            return scts['CL_CUSTOMER_USING_SOFTWARE'].value;
          })
        );
    })
  );

  plateRequired$: Observable<boolean> = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
    filter((emp) => emp != null),
    take(1),
    switchMap((employee) =>
      combineLatest([
        this.systemControlStateService
          .getSystemControlValues(['CL_LICENSE_PLATE_REQUIRED'], employee.rooftopId)
          .pipe(map((scts) => scts['CL_LICENSE_PLATE_REQUIRED']?.value)),
        this.vehicle$,
      ])
    ),
    map(([plateRequiredValue, vehicle]) => {
      const isPlateRequired = plateRequiredValue === '1';
      const plateMissing = !vehicle?.licensePlateNumber || vehicle.licensePlateNumber.trim() === '';
      const isInvalidStatus = vehicle && (vehicle.statusCode === '3' || vehicle.statusCode === '4');
      return isPlateRequired && plateMissing && isInvalidStatus;
    })
  );

  oem$ = this.authenticationStateService.selectAuthenticatedEmployee().pipe(
    filter((employee) => employee != null),
    take(1),
    switchMap((employee) => {
      return this.apiService.get<OemRooftopLink>(`v/1/system/oem-rooftop-links/${employee.rooftopId}`).pipe(
        map((oemRooftopLink: OemRooftopLink) => {
          return oemRooftopLink || null;
        })
      );
    })
  );

  isDashboardSupported$: Observable<boolean> = this.oem$.pipe(
    map(
      (oemRooftopLink: OemRooftopLink) =>
        !!oemRooftopLink && DASHBOARD_SUPPORTED_OEMS.includes(oemRooftopLink.associateId)
    )
  );

  combinedTax$: Observable<CombinedTax> = this.customerUsingSoftware$.pipe(
    switchMap((customerUsingSoftware) => {
      return this.apiService.get<Address>(`v/1/associates/associates/${customerUsingSoftware}/addresses`).pipe(
        switchMap((address: Address[]) => {
          /**
           * We pass start/end date parameters using today's date to get latest, non-expired pst/gst record,
           * no need to pass rooftopId as every rooftop within the province/state would have the same settings
           */
          const pstTaxQueryParams: PstTaxQueryParameters = new PstTaxQueryParameters({
            provinceId: address[0].provStateId,
            startDate: new Date(),
            endDate: new Date(),
          });

          const gstTaxQueryParams: GstTaxQueryParameters = new GstTaxQueryParameters({
            startDate: new Date(),
            endDate: new Date(),
          });
          return combineLatest([
            this.apiService
              .get<PstTax>('v/1/common/pst-taxes', { params: pstTaxQueryParams })
              .pipe(map((pstValues: PstTax[]) => pstValues[0])),
            this.apiService
              .get<GstTax>('v/1/common/gst-taxes', { params: gstTaxQueryParams })
              .pipe(map((gstValues: GstTax[]) => gstValues[0])),
          ]).pipe(
            map(([pstTax, gstTax]) => {
              return { pstTax, gstTax };
            })
          );
        }),
        shareReplay(1)
      );
    })
  );

  oemDashboardData$: any = this.dashboardSubject$.pipe(
    switchMap((data: any) => {
      let responseData: any = {
        dashboardJson: null,
        latestCheckForData: null,
        error: null,
      };
      if (data != null) {
        /**
         * Testing only start
         */
        // let testingParams: any;
        // switch (data.params.vehicleId) {
        //   case 38852:
        //     testingParams = { shdId: 1, oem: 'GM' };
        //     break;
        //   case 38359:
        //     testingParams = { shdId: 2, oem: 'NIS' };
        //     break;
        //   case 8033:
        //     testingParams = { shdId: 4, oem: 'FO' };
        //     break;
        //   case 29706:
        //     testingParams = { shdId: 5, oem: 'CH' };
        //     break;
        //   case 38391:
        //     testingParams = { shdId: 7, oem: 'TOY' };
        //     break;
        //   case 18968:
        //     testingParams = { shdId: 1, oem: 'GM' };
        //     break;
        // }

        // const params = data.params;
        // params.oem = testingParams.oem;

        // const serviceHistoryDashboardQueryParameters: ServiceHistoryDashboardQueryParameters =
        //   new ServiceHistoryDashboardQueryParameters({
        //     odometer: data.params.odometerIn,
        //     convertXmlToJson: true,
        //   });

        // return this.apiService.get(`v/1/vehicles/service-history-dashboards/${testingParams.shdId}`).pipe(
        //   switchMap((shdGet) => {
        //     const updatedServiceHistoryDashboard: ServiceHistoryDashboard = {
        //       ...shdGet,
        //       cachedXmlDateTime: new Date(),
        //     };

        //     return this.apiService.put(
        //       `v/1/vehicles/service-history-dashboards/${testingParams.shdId}`,
        //       updatedServiceHistoryDashboard
        //     );
        //   }),
        //   switchMap((shd) => {
        //     return this.apiService
        //       .get(`/v/1/vehicles/vehicles/${params.vehicleId}/${params.oem}/service-history-dashboards`, {
        //         params: serviceHistoryDashboardQueryParameters,
        //       })
        //       .pipe(
        //         switchMap((serviceHistoryDashboard: ServiceHistoryDashboard) => {
        //           switch (params.oem) {
        //             case 'GM':
        //             case 'FO':
        //               return of({
        //                 ...responseData,
        //                 dashboardJson: serviceHistoryDashboard.dashboardJson,
        //                 latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
        //               });
        //             case 'CH':
        //             case 'NIS':
        //               responseData = {
        //                 dashboard: null,
        //                 latestCheckForData: null,
        //                 error: null,
        //               };
        //               return of({
        //                 ...responseData,
        //                 dashboard: serviceHistoryDashboard.dashboardJson,
        //                 latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
        //               });
        //             case 'TOY':
        //               responseData = {
        //                 campaigns: null,
        //                 latestCheckForData: null,
        //                 error: null,
        //               };
        //               return of({
        //                 ...responseData,
        //                 campaigns: serviceHistoryDashboard.dashboardJson,
        //                 latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
        //               });
        //           }
        //         }),
        //         catchError((error) => {
        //           switch (params.oem) {
        //             case 'GM':
        //             case 'FO':
        //               return of({
        //                 ...responseData,
        //                 latestCheckForData: new Date(),
        //                 error: error,
        //               });
        //             case 'CH':
        //             case 'NIS':
        //               responseData = {
        //                 dashboard: null,
        //                 latestCheckForData: null,
        //                 error: null,
        //               };

        //               return of({
        //                 ...responseData,
        //                 latestCheckForData: new Date(),
        //                 error: error,
        //               });
        //             case 'TOY':
        //               responseData = {
        //                 dashboardJson: null,
        //                 latestCheckForData: null,
        //                 error: null,
        //               };

        //               return of({
        //                 ...responseData,
        //                 latestCheckForData: new Date(),
        //                 error: error,
        //               });
        //           }
        //         })
        //       );
        //   })
        // );

        /**
         * Testing only end
         */

        const params = data.params;
        const serviceHistoryDashboardQueryParameters: ServiceHistoryDashboardQueryParameters =
          new ServiceHistoryDashboardQueryParameters({
            odometer: data.params.odometerIn,
            convertXmlToJson: true,
          });

        return this.apiService
          .get(`/v/1/vehicles/vehicles/${params.vehicleId}/${params.oem}/service-history-dashboards`, {
            params: serviceHistoryDashboardQueryParameters,
          })
          .pipe(
            switchMap((serviceHistoryDashboard: ServiceHistoryDashboard) => {
              switch (params.oem) {
                case 'GM':
                case 'FO':
                  return of({
                    ...responseData,
                    dashboardJson: serviceHistoryDashboard.dashboardJson,
                    latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
                  });
                case 'CH':
                case 'NIS':
                  responseData = {
                    dashboard: null,
                    latestCheckForData: null,
                    error: null,
                  };
                  return of({
                    ...responseData,
                    dashboard: serviceHistoryDashboard.dashboardJson,
                    latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
                  });
                case 'TOY':
                  responseData = {
                    campaigns: null,
                    latestCheckForData: null,
                    error: null,
                  };
                  return of({
                    ...responseData,
                    campaigns: serviceHistoryDashboard.dashboardJson,
                    latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
                  });
                default:
                  return of({
                    ...responseData,
                    dashboardJson: serviceHistoryDashboard.dashboardJson,
                    latestCheckForData: serviceHistoryDashboard.cachedXmlDateTime,
                  });
              }
            }),
            catchError((error) => {
              switch (params.oem) {
                case 'GM':
                case 'FO':
                  return of({
                    ...responseData,
                    latestCheckForData: new Date(),
                    error: error,
                  });
                case 'CH':
                case 'NIS':
                  responseData = {
                    dashboard: null,
                    latestCheckForData: null,
                    error: null,
                  };

                  return of({
                    ...responseData,
                    latestCheckForData: new Date(),
                    error: error,
                  });
                case 'TOY':
                  responseData = {
                    dashboardJson: null,
                    latestCheckForData: null,
                    error: null,
                  };

                  return of({
                    ...responseData,
                    latestCheckForData: new Date(),
                    error: error,
                  });
                default:
                  return of({
                    ...responseData,
                    latestCheckForData: new Date(),
                    error: error,
                  });
              }
            })
          );
      } else {
        return of(null);
      }
    }),
    shareReplay(1)
  );

  hyundaiDashboardData$: any = this.dashboardSubject$.pipe(
    switchMap((data: any) => {
      if (data != null) {
        const params = data.params;

        if (params.vehicleManufacturer == 'HYU' || params.vehicleManufacturer == 'GEN') {
          let headers: HttpHeaders = new HttpHeaders();
          headers = headers.append('client_api_key', 'C53jiaePs0XuCN1dwYn+TMrtmZkgZLyhODuI8H3OubI=');
          headers = headers.append('client_app_id', 'f0dde5a9-3082-4ea9-b60a-1c7967808eff');

          const responseData: any = {
            dashboard: null,
            latestCheckForData: null,
            error: null,
          };

          return combineLatest([
            this.oemIntegrationService.get(`hyundai/recalls/recall-lookup/en/HYN/${params.vehicleVin}`, {
              headers: headers,
            }),
            this.oemIntegrationService
              .get(`hyundai/recalls/service-campaign-lookup/en/HYN/${params.vehicleVin}`, {
                headers: headers,
              })
              .pipe(
                catchError(() => {
                  return of({});
                })
              ),
            this.oemIntegrationService.get(`hyundai/vehicles/vin-details/en/HYN/${params.vehicleVin}`, {
              headers: headers,
            }),
            this.oemIntegrationService
              .get(`hyundai/vehicle-warranty/warranty-details/en/HYN/${params.vehicleVin}/${params.vehicleYear}`, {
                headers: headers,
              })
              .pipe(
                catchError(() => {
                  return of({ ServiceCampaigns: [], Errors: null });
                })
              ),
            this.oemIntegrationService
              .get(`hyundai/recalls/recall-lookup/en/HYN/${params.vehicleVin}`, {
                headers: headers,
              })
              .pipe(
                switchMap((recalls): any => {
                  if (recalls.RecallCampaigns.length > 0) {
                    const recallCampaignIds: string[] = recalls.RecallCampaigns.map(
                      (campaign: any) => campaign.CampaignId
                    );

                    return forkJoin(
                      recallCampaignIds.map((recallCampaignId) =>
                        this.oemIntegrationService.get(
                          `hyundai/campaigns/campaign-parts-lookup/en/${recallCampaignId}`,
                          {
                            headers: headers,
                          }
                        )
                      )
                    ).pipe(
                      map((results) => {
                        return results.map((recallParts, i) => ({
                          recallCampaignId: recallCampaignIds[i],
                          recallCampaignParts: recallParts,
                        }));
                      })
                    );
                  } else {
                    return of([]);
                  }
                })
              ),
            this.oemIntegrationService
              .get(`hyundai/recalls/last-refreshed`, {
                headers: headers,
              })
              .pipe(
                catchError(() => {
                  return of({});
                })
              ),
          ]).pipe(
            map(([recallData, campaignData, vehicleData, warrantyData, recallCampaignParts, recallLastRefreshed]) => {
              return {
                recallCampaigns: recallData.RecallCampaigns,
                recallCampaignError: recallData.Errors,
                serviceCampaigns: campaignData.ServiceCampaigns,
                serviceCampaignError: campaignData.Errors,
                vehicleData: vehicleData,
                warrantyData: warrantyData,
                recallCampaignParts: recallCampaignParts,
                recallLastRefreshed,
              };
            }),
            switchMap((dashboardData: any) => {
              return of({
                ...responseData,
                dashboard: dashboardData,
                latestCheckForData: new Date(),
              });
            }),
            catchError((error) => {
              return of({
                ...responseData,
                latestCheckForData: new Date(),
                error: error,
              });
            })
          );
        } else {
          this.noHyundaiDashboardData$.next(true);
          return of(null);
        }
      } else {
        return of(null);
      }
    }),
    shareReplay(1)
  );

  acceptQuoteFromMenu$ = this.actions$.pipe(
    ofType(ADD_PENDING_ACCEPTED_SERVICE_IN_STATE),
    map((action: AddPendingAcceptedServiceInState) => {
      return action.payload.serviceGroup;
    })
  );

  acceptQuoteFromToyotaMenu$ = new Subject<any>();
  declineQuoteFromToyotaMenu$ = new Subject<any>();

  acceptServiceFromHyundaiMenu$ = new Subject<any>();
  removeAcceptedServiceFromHyundaiMenu$ = new Subject<any>();
  declineServiceFromHyundaiMenu$ = new Subject<any>();
  updateDeclinedServiceFromHyundaiMenu$ = new Subject<any>();
  removeDeclinedServiceFromHyundaiMenu$ = new Subject<any>();
  disableHyundaiMenuRemoveServiceButton$: Subject<boolean> = new Subject<false>();
  disableHyundaiMenuAddServiceButton$: Subject<boolean> = new Subject<false>();

  quoteLineToUpdateAfterCreation$ = new Subject<any>();
  addQuoteLineFromMenu$ = new Subject<any>();
  resetApprovalTabData$: Subject<boolean> = new Subject<boolean>();
  updateAppointmentCheckInStatus$ = this.actions$.pipe(
    ofType(AppointmentsEntityActionTypes.UpdateAppointmentOnServerSuccess),
    tap((action: UpdateAppointmentOnServerSuccess) => {
      this.updateTodaysAppointmentInState(action.payload);
      this.updateUnacknowledgedAppointmentInState(action.payload);
    })
  );
  workOrderId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  inspectionDeletedPreviously$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  http: any;

  constructor(
    private store: Store<ServiceLanePartialState>,
    private actions$: Actions,
    private router: Router,
    private apiService: ApiService,
    private oemIntegrationService: OemIntegrationService,
    private communicatorFacade: CommunicatorFacade,
    private authenticationStateService: AuthenticationStateService,
    private systemControlStateService: SystemControlStateService
  ) {}

  getQuoteLinesGeneratedFromCurrentTransaction(appointmentId: number, workOrderId: number) {
    return this.store.pipe(
      select(serviceLaneQuery.quoteLinesGeneratedFromCurrentTransaction(appointmentId, workOrderId))
    );
  }

  quoteLineWithServiceGroupExists(serviceGroupId: string, appointmentId: number) {
    return this.store.pipe(select(serviceLaneQuery.quoteLineWithServiceGroupExists, { serviceGroupId, appointmentId }));
  }

  activeQuoteLineWithServiceGroupExists(serviceGroupId: string) {
    return this.store.pipe(select(serviceLaneQuery.activeQuoteLineWithServiceGroupExists(serviceGroupId)));
  }

  quoteLineWithOpCodeExists(opCode: string, appointmentId: number) {
    return this.store.pipe(select(serviceLaneQuery.quoteLineWithOpCodeExists(opCode, appointmentId)));
  }

  getQuoteLineActionedPercentages(appointmentId: number, workOrderId: number) {
    return this.store.pipe(select(serviceLaneQuery.getQuoteLineActionedPercentages, { appointmentId, workOrderId }));
  }
  approveQuoteLine(quoteLine: QuoteDetail) {
    this.store.dispatch(new AddApprovedQuoteLineInState(quoteLine));
  }

  updateApprovedQuoteLine(quoteLine: QuoteDetail, previousId: number) {
    this.store.dispatch(new UpdateApprovedQuoteLineInState({ quoteLine, previousId }));
  }

  updateDeclinedQuoteLine(quoteLine: QuoteDetail, previousId: number) {
    this.store.dispatch(new UpdateDeclinedQuoteLineInState({ quoteLine, previousId }));
  }

  updateDeclinedReasonForQuoteLineInState(quoteLine: QuoteDetail) {
    this.store.dispatch(new UpdateDeclinedReasonForQuoteLineInState({ quoteLine }));
  }

  updateDeclinedReasonForQuoteLineOnServer(quoteLine: QuoteDetail) {
    this.store.dispatch(new UpdateDeclinedReasonForQuoteLineOnServer(quoteLine));
  }

  updateDeclinedReasonForQuoteLineOnServerSuccess(quoteLine: QuoteDetail) {
    this.store.dispatch(new UpdateDeclinedReasonForQuoteLineOnServerSuccess(quoteLine));
  }

  declineQuoteLine(quoteLine: QuoteDetail) {
    this.store.dispatch(new AddDeclinedQuoteLineInState(quoteLine));
  }

  removeApprovedQuoteLine(quoteLine: QuoteDetail) {
    this.store.dispatch(new RemoveApprovedQuoteLineInState(quoteLine));
  }

  removeDeclinedQuoteLine(quoteLine: QuoteDetail) {
    this.store.dispatch(new RemoveDeclinedQuoteLineInState(quoteLine));
  }
  addQuoteLine(quoteLine: QuoteDetail) {
    this.store.dispatch(new GetQuoteLinesFromServerSuccess([quoteLine]));
  }

  createQuoteLineOnServer(quoteLine: QuoteDetail) {
    this.store.dispatch(
      new CreateQuoteLineOnServer({
        quoteLine,
        queryParams: new QuoteDetailQueryParameters({ embed: 'employeeAssociate,serviceGroup' }),
      })
    );
  }

  deleteQuoteLineFromServer(quoteLine: QuoteDetail) {
    this.store.dispatch(
      new DeleteQuoteLineFromServer({
        quoteLine,
      })
    );
  }

  triggerCreateWorkOrderProcess() {
    this.store.dispatch(new TriggerCreateWorkOrderProcess({}));
  }
  setVehicle(vehicle: Vehicle) {
    this.store.dispatch(new SetVehicle(vehicle));
  }

  setVerifyCustomerCreateWOButtonEnabledInState(status: boolean) {
    this.store.dispatch(new SetVerifyCustomerCreateWOButtonEnabledInState(status));
  }

  setWorkOrderRequiredFieldsPresentInState(status: {
    odometerIn: boolean;
    serviceAdvisorId: boolean;
    serviceCode: boolean;
  }) {
    this.store.dispatch(new SetWorkOrderRequiredFieldsPresentInState(status));
  }

  setUnsavedLineItemInState(status: boolean) {
    this.store.dispatch(new SetUnsavedLineItemInState(status));
  }

  getSelectedAppointment(appointmentQueryParameters: AppointmentQueryParameters) {
    this.store.dispatch(new GetSelectedAppointmentFromServer(appointmentQueryParameters));
  }

  setSelectedAppointmentCases(appointmentCases: AppointmentCase[]) {
    this.store.dispatch(new GetSelectedAppointmentCasesFromServerSuccess({ appointmentCases }));
  }

  updateQuoteLine(quoteLine: QuoteDetail) {
    this.store.dispatch(new ServiceLaneQuoteUpdate(quoteLine));
  }

  updateQuoteLineOnServer(quoteLine: QuoteDetail) {
    this.store.dispatch(new UpdateQuoteLineOnServer(quoteLine));
  }

  updateQuoteLineInState(quoteLine: QuoteDetail) {
    this.store.dispatch(new UpdateQuoteLineInState(quoteLine));
  }

  getQuoteLinesFromServer(quoteLineQueryParams: QuoteDetailQueryParameters) {
    this.store.dispatch(new GetQuoteLinesFromServer(quoteLineQueryParams));
  }

  updateSelectedAppointmentCaseOnServer(appointmentCase: AppointmentCase) {
    this.store.dispatch(new UpdateAppointmentCaseOnServer(appointmentCase));
  }

  private updateSelectedAppointmentCase(appointmentCase: AppointmentCase) {
    this.store.dispatch(new UpdateSelectedAppointmentCaseOnServerSuccess({ appointmentCase }));
  }

  deleteSelectedAppointmentCaseOnServer(appointmentCase: AppointmentCase) {
    this.store.dispatch(new DeleteAppointmentCaseOnServer(appointmentCase));
  }

  private deleteSelectedAppointmentCase(appointmentCase: AppointmentCase) {
    this.store.dispatch(new DeleteSelectedAppointmentCaseOnServerSuccess({ appointmentCase }));
  }

  deleteSelectedAppointmentDeclinedMpviFromServer(
    serviceAppointmentDeclinedMpviCode: ServiceAppointmentDeclinedMpviCode
  ) {
    this.store.dispatch(new DeleteSelectedAppointmentDeclinedMpviFromServer(serviceAppointmentDeclinedMpviCode));
  }

  createAppointmentCaseFromQuoteLine(quoteLine: QuoteDetail, appointmentId: number) {
    this.store.dispatch(new CreateAppointmentCaseFromQuoteLine({ quoteLine, appointmentId }));
  }

  createAppointmentCasesFromQuoteLines(quoteHeaderId: number, quoteLineSequences: string, appointmentId: number) {
    this.store.dispatch(new CreateAppointmentCasesFromQuoteLines({ quoteHeaderId, quoteLineSequences, appointmentId }));
  }

  appointmentCaseWithOpCodeExists(opCode: string, appointmentId: number) {
    return this.store.pipe(select(serviceLaneQuery.appointmentCaseWithOpCodeExists(opCode, appointmentId)));
  }

  createPartDetailForQuoteLine(partDetails: QuoteSalesDetail, quoteLine: QuoteDetail) {
    this.store.dispatch(new CreatePartDetailForQuoteLine({ partDetails, quoteLine }));
  }

  createPartsRequestDetailForQuoteLine(
    partsRequest: PartsRequest,
    partsRequestDetail: PartsRequestDetail,
    quoteLine: QuoteDetail
  ) {
    this.store.dispatch(new CreatePartsRequestDetailForQuoteLine({ partsRequest, partsRequestDetail, quoteLine }));
  }

  createAppointmentCaseOnServer(appointmentCase: AppointmentCase, quoteLine: QuoteDetail) {
    this.store.dispatch(new CreateAppointmentCaseOnServer({ appointmentCase, quoteLine }));
  }

  private createAppointmentCase(appointmentCase: AppointmentCase, quoteLine: QuoteDetail) {
    this.store.dispatch(new CreateAppointmentCaseOnServerSuccess({ appointmentCase, quoteLine }));
  }

  createAppointment(appointment: Appointment) {
    this.store.dispatch(new CreateAppointmentOnServerSuccess2(appointment));
  }

  createAppointmentOnServer(appointment: Appointment, appointmentQueryParams: AppointmentQueryParameters) {
    this.store.dispatch(new CreateAppointmentOnServer({ appointment, appointmentQueryParams }));
  }

  updateAppointmentOnServer(appointment: Appointment, appointmentQueryParams: AppointmentQueryParameters) {
    this.store.dispatch(new UpdateAppointmentOnServer({ appointment, appointmentQueryParams }));
  }

  updateAppointment(appointment: Appointment) {
    this.store.dispatch(new UpdateSelectedAppointmentOnServerSuccess(appointment));
  }

  deleteServiceAppointmentFromServer(appointment: Appointment) {
    this.store.dispatch(new DeleteAppointmentFromServer({ appointment: appointment }));
  }

  createWorkOrderCaseOnServer(
    workOrderCase: WorkOrderCase,
    servicePricing: any,
    vehicle: Vehicle,
    workOrder: WorkOrder
  ) {
    this.store.dispatch(new CreateWorkOrderCaseOnServer({ workOrderCase, servicePricing, vehicle, workOrder }));
  }

  deleteWorkOrderCaseFromServer(workOrderCase: WorkOrderCase) {
    this.store.dispatch(new DeleteWorkOrderCaseFromServer(workOrderCase));
  }

  createWorkOrderDeclinedOperationOnServer(workOrderDeclinedOperation: WorkorderDeclinedMpviCode) {
    this.store.dispatch(new CreateWorkOrderDeclinedOperationsOnServer(workOrderDeclinedOperation));
  }

  updateWorkOrderDeclinedOperationOnServer(workOrderDeclinedOperation: WorkorderDeclinedMpviCode) {
    this.store.dispatch(new UpdateWorkOrderDeclinedOperationOnServer(workOrderDeclinedOperation));
  }

  deleteWorkOrderDeclinedOperationFromServer(workOrderDeclinedOperation: WorkorderDeclinedMpviCode) {
    this.store.dispatch(new DeleteWorkOrderDeclinedOperationFromServer(workOrderDeclinedOperation));
  }

  private deleteAppointment(appointment: Appointment) {
    this.store.dispatch(new DeleteAppointmentFromServerSuccess2({ appointment: appointment }));
  }

  deleteTodaysAppointmentFromServer(appointment: Appointment) {
    this.store.dispatch(new DeleteAppointmentFromServer({ appointment: appointment }));
  }

  private deleteTodaysAppointment(appointment: Appointment) {
    this.store.dispatch(new DeleteTodaysAppointmentFromServerSuccess({ appointment: appointment }));
  }

  private updateAssociate(associate: Associate) {
    this.store.dispatch(new UpdateCustomerAssociateOnServerSuccess(associate));
  }

  updateAssociateOnServer(associate: Associate) {
    this.store.dispatch(new UpdateAssociateOnServer(associate));
  }

  private updateAddress(address: Address) {
    this.store.dispatch(new UpdateCustomerAddressOnServerSuccess(address));
  }

  updateAddressOnServer(address: Address) {
    this.store.dispatch(
      new UpdateAddressOnServer({
        address: address,
        queryParams: new AddressQueryParameters({ embed: ['associate', 'location'] }),
      })
    );
  }

  getAllNotes(associateId: string, appointmentId: string, vehicleId: string) {
    this.store.dispatch(new GetAllNotesFromServer({ associateId, appointmentId, vehicleId }));
  }

  createNotes(newNote: Note, noteType: string) {
    this.store.dispatch(new CreateNotesOnServer({ newNote, noteType }));
  }

  updateNotes(newNote: Note, noteType: string) {
    this.store.dispatch(new UpdateNotesOnServer({ newNote, noteType }));
  }

  clearState() {
    this.store.dispatch(new ClearCustomerAndVehicleState({}));
  }

  clearAppointmentState() {
    this.store.dispatch(new ClearAppointmentState({}));
  }

  getAppointments(appointmentQueryParams: AppointmentQueryParameters, employee: Employee) {
    this.store.dispatch(new GetTodaysAppointmentsFromServer(appointmentQueryParams));
  }

  GetUnacknowledgedAppointmentsFromServer(appointmentQueryParameters: AppointmentQueryParameters) {
    this.store.dispatch(new GetUnacknowledgedAppointmentsFromServer(appointmentQueryParameters));
  }

  createVehicleOnServer(vehicle: Vehicle) {
    this.store.dispatch(new CreateVehicleOnServer(vehicle));
  }

  createMessageTransactionMessagePreferenceOnServer(
    messageTransactionMessagePreference: MessageTransactionMessagePreference
  ) {
    this.store.dispatch(
      new CreateMessageTransactionMessagePreferenceOnServer({
        messageTransactionMessagePreference,
      })
    );
  }

  //Observable
  _createMessageTransactionMessagePreferenceOnServer(
    messageTransactionMessagePreference: MessageTransactionMessagePreference
  ): Observable<any> {
    this.store.dispatch(new CreateMessageTransactionMessagePreferenceOnServer({ messageTransactionMessagePreference }));

    return this.actions$.pipe(
      ofType(
        MessageTransactionMessagePreferenceEntityActionTypes.CreateMessageTransactionMessagePreferenceOnServerSuccess
      ),
      take(1)
    );
  }

  updateMessageTransactionMessagePreferenceOnServer(
    messageTransactionMessagePreference: MessageTransactionMessagePreference,
    messageTransactionMessagePreferenceQueryParams: MessageTransactionMessagePreferenceQueryParameters
  ) {
    this.store.dispatch(
      new UpdateMessageTransactionMessagePreferenceOnServer({
        messageTransactionMessagePreference,
        messageTransactionMessagePreferenceQueryParams,
      })
    );
  }

  //Obseravble
  _updateMessageTransactionMessagePreferenceOnServer(
    messageTransactionMessagePreference: MessageTransactionMessagePreference,
    queryParams: MessageTransactionMessagePreferenceQueryParameters
  ): Observable<any> {
    this.store.dispatch(
      new UpdateMessageTransactionMessagePreferenceOnServer({
        messageTransactionMessagePreference,
        messageTransactionMessagePreferenceQueryParams: queryParams,
      })
    );

    return this.actions$.pipe(
      ofType(
        MessageTransactionMessagePreferenceEntityActionTypes.UpdateMessageTransactionMessagePreferenceOnServerSuccess
      ),
      take(1)
    );
  }

  createVehicleWithAssociateAndAddressOnServer(
    vehicle: Vehicle,
    associate: Associate,
    address: Address,
    vehicleQueryParams: any
  ) {
    this.store.dispatch(
      new CreateVehicleWithAssociateAndAddressFromServer({ vehicle, associate, address, vehicleQueryParams })
    );
  }

  updateVehicleOnServer(vehicle: Vehicle) {
    this.store.dispatch(new UpdateVehicleOnServer(vehicle));
  }

  getVehicleFromServer(vehicleId: number) {
    this.store.dispatch(new GetVehicleFromServer({ params: { id: vehicleId } }));
  }

  updateAppointmentFiltersInState(appointmentFilters: any) {
    this.store.dispatch(new UpdateAppointmentFiltersInState(appointmentFilters));
  }

  addToast(toastObject: any) {
    this.toastSubject.next(toastObject);
  }

  listenForToasts() {
    return this.toastSubject;
  }

  removeQuoteLineFromState(quoteLine: QuoteDetail) {
    this.store.dispatch(new RemoveQuoteFromState(quoteLine));
  }

  isEmpty(obj: any) {
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  }

  selectServiceGroup(serviceGroupId: string) {
    return this.store.pipe(
      select(serviceLaneQuery.getServiceGroups),
      filter((serviceGroup: any) => {
        return !this.isEmpty(serviceGroup);
      }),
      map((serviceGroups: any) => {
        return serviceGroups.find((serviceGroup: any) => serviceGroup.id === serviceGroupId);
      })
    );
  }

  createServiceAppointmentDeclinedMPVICode(declinedOperation: ServiceAppointmentDeclinedMpviCode) {
    this.store.dispatch(new CreateServiceAppointmentDeclinedOperationsOnServer(declinedOperation));
  }

  updateSelectedAppointmentDeclinedMpviOnServer(declinedOperation: ServiceAppointmentDeclinedMpviCode) {
    this.store.dispatch(new UpdateSelectedAppointmentDeclinedMpviOnServer(declinedOperation));
  }

  selectTodaysAppointmentById(appointmentId: number) {
    return this.todaysAppointments$.pipe(
      map((apt: Appointment[]) => {
        return apt.find((ap) => ap.id == appointmentId);
      }),
      take(1)
    );
  }

  selectUnacknowledgedAppointmentById(appointmentId: number) {
    return this.unacknowledgedAppointmentCards$.pipe(
      map((apt: Appointment[]) => {
        return apt.find((ap) => ap.id == appointmentId);
      }),
      take(1)
    );
  }

  updateTodaysAppointmentInState(appointment: Appointment) {
    this.store.dispatch(new UpdateTodaysAppointmentInState({ appointment }));
  }

  updateUnacknowledgedAppointmentInState(appointment: Appointment) {
    this.store.dispatch(new UpdateUnacknowledgedAppointmentInState({ appointment }));
  }

  createToyotaCampaignArray(campaigns: any): any[] {
    let campaignArray: any[];
    if (
      campaigns?.ShowCampaignInquiry?.Body?.Campaign &&
      Array.isArray(campaigns?.ShowCampaignInquiry?.Body?.Campaign)
    ) {
      campaignArray = campaigns.ShowCampaignInquiry.Body.Campaign.map((campaign: any) => {
        return {
          campaignNumber: campaign.CampaignNumber._text,
          opCode: campaign.CampaignOperationCode._text,
          opCodeDescription: campaign.OperationCodeDescription[0]._text,
          allowanceHours: campaign.AllowanceHours._text,
          complaint: campaign.Complaint[0]._text ? campaign.Complaint[0]._text : '',
          cause: campaign.Cause[0]._text ? campaign.Cause[0]._text : '',
          correction: campaign.Correction[0]._text ? campaign.Correction[0]._text : '',
        };
      });
    } else {
      if (Object.keys(campaigns?.ShowCampaignInquiry?.Body).length > 0) {
        campaignArray = [
          {
            campaignNumber: campaigns.ShowCampaignInquiry.Body.Campaign.CampaignNumber._text,
            opCode: campaigns.ShowCampaignInquiry.Body.Campaign.CampaignOperationCode._text,
            opCodeDescription: campaigns.ShowCampaignInquiry.Body.Campaign.OperationCodeDescription[0]._text,
            allowanceHours: campaigns.ShowCampaignInquiry.Body.Campaign.AllowanceHours._text,
            complaint: campaigns.ShowCampaignInquiry.Body.Campaign.Complaint[0]._text
              ? campaigns.ShowCampaignInquiry.Body.Campaign.Complaint[0]._text
              : '',
            cause: campaigns.ShowCampaignInquiry.Body.Campaign.Cause[0]._text
              ? campaigns.Body.Campaign.Cause[0]._text
              : '',
            correction: campaigns.ShowCampaignInquiry.Body.Campaign.Correction[0]._text
              ? campaigns.Body.Campaign.Correction[0]._text
              : '',
          },
        ];
      } else {
        campaignArray = [];
      }
    }
    return campaignArray;
  }

  createFordParsedDashboard(fordDashboard: any) {
    const parsedDashboard = JSON.parse(
      convert.xml2json(fordDashboard, {
        compact: true,
        trim: true,
        ignoreComment: true,
        spaces: 1,
      })
    )['AutoBOD']['Body']['ShowVehicleServiceHistory']['VehicleInformation'];

    if (
      !Array.isArray(parsedDashboard?.ServiceCampaign?.Campaign) &&
      !parsedDashboard?.ServiceCampaign?.SystemMessage
    ) {
      const campaignArray = [parsedDashboard?.ServiceCampaign?.Campaign];

      parsedDashboard.ServiceCampaign.Campaign = campaignArray;
    }

    if (!Array.isArray(parsedDashboard?.ServiceHistory?.Service) && !parsedDashboard?.ServiceHistory?.SystemMessage) {
      const services = [parsedDashboard?.ServiceHistory?.Service];
      parsedDashboard.ServiceHistory.Service = services;
    }

    if (Array.isArray(parsedDashboard.ServiceCampaign.Campaign)) {
      parsedDashboard.ServiceCampaign.Campaign = parsedDashboard.ServiceCampaign.Campaign.map((camp: any) => {
        let concatCampaignDescription = '';
        if (Array.isArray(camp.CampaignDescription)) {
          camp.CampaignDescription.map((campDesc: { _text: string }) => {
            concatCampaignDescription = concatCampaignDescription.concat(campDesc._text, ' ');
          });
        } else {
          concatCampaignDescription = camp.CampaignDescription._text;
        }

        return {
          CampaignDescription: concatCampaignDescription,
          CampaignId: camp.CampaignId._text,
        };
      });
    }
    return parsedDashboard;
  }

  sendServiceAppointmentConfirmation(
    appointment: Appointment,
    systemControls: any,
    authenticatedUser: TokenUser,
    associate: Associate
  ) {
    if (systemControls['CL_COMMUNICATOR_ENABLE'].value === '1') {
      let conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
        transactionTypeId: 6,
        messageTypeId: 5,
        transactionId: appointment.id,
      });

      let apptDate = new Date(appointment.dateTime).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      let apptTime = new Date(appointment.dateTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

      let event: Event = new Event({
        conversationId: null,
        memberId: authenticatedUser.id.toString(),
        eventTypeId: EventTypes.Message,
        content: `You have booked a service appointment at ${systemControls['CL_CUSTOMER_USING_SOFTWARE'].displayValue} for ${apptDate}. Drop off time is ${apptTime}. Thank-you!`,
        guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        sentDate: new Date(),
        sent: false,
        sendToCustomer: true,
        id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
      });

      let conversation: Conversation = new Conversation({
        dmsMessageTypeId: 5,
        dmsTransactionTypeId: 6,
        dmsTransactionId: appointment.id,
        createdBy: authenticatedUser.id,
      });

      let members: Member[] = [];
      members.push(
        new Member({
          userId: authenticatedUser.id.toString(),
          avatarUrl: authenticatedUser.avatarUrl,
          firstName: authenticatedUser.firstName,
          lastName: authenticatedUser.lastName,
          nickname: authenticatedUser.firstName,
          isEmployee: true,
          isRead: true,
          isArchived: false,
          isExcluded: false,
          isPinnedToUi: false,
        })
      );

      members.push(
        new Member({
          userId: associate.id,
          firstName: associate.firstName,
          lastName: associate.lastName,
          nickname: associate.firstName,
          isEmployee: false,
          isRead: true,
          isArchived: false,
          isExcluded: false,
          isPinnedToUi: false,
        })
      );

      let userParams: UserQueryParameters = new UserQueryParameters({
        username: `Communicator${authenticatedUser.destination}`,
      });

      this.apiService
        .get<any>('quorum/users', { params: userParams })
        .pipe(
          filter((users) => users),
          map((users) => {
            const user = users[0];

            const member: Member = new Member();
            member.userId = user.id.toString();
            member.avatarUrl = user.avatarUrl;
            member.firstName = user.firstName;
            member.lastName = user.lastName;
            member.nickname = user.firstName;
            member.isEmployee = true;
            member.isRead = true;
            member.isArchived = false;
            member.isExcluded = false;
            member.isPinnedToUi = false;
            members.push(member);
          })
        )
        .subscribe(() => {
          let optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'true' });

          this.communicatorFacade.sendCommunicatorMessage(
            conversationQueryParameters,
            conversation,
            members,
            event,
            optionalHeader
          );
        });
    }
  }

  getWorkOrderMPVIQuoteLines(workOrderId: number) {
    return this.store.pipe(select(serviceLaneQuery.getWorkOrderMPVIQuoteLines, { workOrderId }));
  }

  determineSalesType(warrantySubmissionTypeId: string) {
    let salesType: string;
    switch (warrantySubmissionTypeId) {
      case 'CP':
        salesType = 'R';
        break;
      case 'SN':
      case 'CC':
      case 'TC':
      case 'WC':
      case 'MM':
      case 'DP':
      case 'MS':
        salesType = 'W';
        break;
      case 'IN':
        salesType = 'I';
        break;
      case 'EC':
        salesType = 'EW';
        break;
      case 'C2':
        salesType = 'W2';
        break;
      case 'C3':
        salesType = 'W3';
        break;
      case 'C4':
        salesType = 'W4';
        break;
      case 'W2':
      case 'W3':
      case 'W4':
      case 'P2':
      case 'P3':
      case 'P4':
      case 'E2':
      case 'E3':
      case 'E4':
      case 'FM':
      case 'TW':
      case 'PM':
        salesType = warrantySubmissionTypeId == null ? '' : warrantySubmissionTypeId;
        break;
    }
    return salesType;
  }

  isToday(date: Date) {
    const today = new Date();
    const dateToCheck = new Date(date);
    return (
      dateToCheck.getDate() == today.getDate() &&
      dateToCheck.getMonth() == today.getMonth() &&
      dateToCheck.getFullYear() == today.getFullYear()
    );
  }

  getAlternateTransportations(alternateTransportationQueryParameters: AlternateTransportationQueryParameters) {
    this.store.dispatch(new GetAlternateTransportationsFromServer(alternateTransportationQueryParameters));
  }

  createAlternateTransportation(alternateTransportation: AlternateTransportation) {
    this.store.dispatch(new CreateAlternateTransportationOnServer(alternateTransportation));
  }

  updateAlternateTransportation(alternateTransportation: AlternateTransportation) {
    this.store.dispatch(new UpdateAlternateTransportationOnServer(alternateTransportation));
  }

  deleteAlternateTransportation(alternateTransportation: AlternateTransportation) {
    this.store.dispatch(new DeleteAlternateTransportationOnServer(alternateTransportation));
  }

  isLeapYear(today: Date) {
    if ((today.getFullYear() % 4 == 0 && today.getFullYear() % 100 != 0) || today.getFullYear() % 400 == 0) {
      return true;
    } else {
      return false;
    }
  }

  generateUrl(basePath: string, params: Record<string, string | number | null | undefined>): string {
    const pathQueryParams = new URLSearchParams();

    for (const key in params) {
      const value = params[key];
      // Avoid truthy here as 0 and '' maybe be valid query params
      if (value !== null && value !== undefined) {
        pathQueryParams.append(key, String(value));
      }
    }

    return `${basePath}?${pathQueryParams.toString()}`;
  }

  navigateToWorkOrderConfirmation(workOrderId: number, customerName: string): void {
    const url = this.generateUrl('create-workorder-confirmation', {
      workOrderId,
      customerName,
    });
    this.router.navigateByUrl(url);
  }

  getQuoteLines(): Observable<QuoteDetail[]> {
    return this.store.pipe(select(serviceLaneQuery.getQuoteLines));
  }

  createQuoteHeader(quoteHeader: QuoteHeader): Observable<QuoteHeader> {
    return this.apiService.post<QuoteHeader>('v/1/fixed-operations/quote-headers', quoteHeader);
  }
}
